import React, { useState } from 'react';
import SEO from '../components/SEO';
import PageHero, { CTABanner } from '../components/PageHero';
import { services } from '../data/mock';
import {
  Calculator, Receipt, ShieldCheck, FileText, Scale, GraduationCap,
  Target, Code2, Search, Workflow, TrendingUp, Rocket, Users, Truck, ClipboardCheck, Check,
} from 'lucide-react';

const iconMap = {
  Calculator, Receipt, ShieldCheck, FileText, Scale, GraduationCap,
  Target, Code2, Search, Workflow, TrendingUp, Rocket, Users, Truck, ClipboardCheck,
};

const categories = ['All', 'Finance', 'Compliance', 'Strategy', 'Technology', 'Operations', 'Development', 'Assurance'];

export default function ServicesPage() {
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? services : services.filter((s) => s.category === filter);

  return (
    <>
      <SEO
        title="Services"
        description="Fifteen specialized service lines including accounting, tax, audit, legal advisory, ERP, capital raising, corporate strategy, HR, procurement, and monitoring & evaluation."
        path="/services"
        keywords="consulting services Somalia, accounting Mogadishu, tax compliance, audit services, ERP customization, capital raising, M&E, due diligence, business advisory"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Services — Salihi Consulting',
          url: 'https://salihiconsulting.com/services',
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: services.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              item: {
                '@type': 'Service',
                name: s.title,
                description: s.short,
                provider: { '@id': 'https://salihiconsulting.com/#organization' },
                serviceType: s.category,
              },
            })),
          },
        }}
      />
      <PageHero
        breadcrumb="Services"
        eyebrow="What we do"
        title="Fifteen specialized service lines."
        italicTitle="One integrated team."
        subtitle="From boardroom strategy to back-office execution, Salihi Consulting Services delivers the full spectrum of capabilities modern organizations require to grow with confidence, comply with rigor, and outperform their peers."
      />

      {/* Filter */}
      <section className="bg-[#0a1b35] py-10 border-b border-white/8 sticky top-[78px] z-30 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-2 overflow-x-auto">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-[12.5px] font-medium tracking-wide transition-all duration-300 border whitespace-nowrap ${
                  filter === c
                    ? 'bg-[#e8b863] text-[#0a1b35] border-[#e8b863] shadow-lg shadow-[#e8b863]/20'
                    : 'border-white/18 text-white/78 hover:border-[#e8b863]/55 hover:text-white'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Service details */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 space-y-10">
          {filtered.map((s, idx) => {
            const Icon = iconMap[s.icon] || Calculator;
            const reversed = idx % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                className="grid lg:grid-cols-12 gap-8 lg:gap-12 bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-3xl p-7 sm:p-9 lg:p-12 hover:border-[#e8b863]/30 transition-colors"
              >
                <div className={`lg:col-span-7 ${reversed ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="font-serif text-[#e8b863]/55 text-[34px] leading-none">{s.num}</span>
                    <span className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold">
                      {s.category}
                    </span>
                  </div>
                  <h2 className="font-serif text-white font-medium text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.12] tracking-tight">
                    {s.title}
                  </h2>
                  <div className="mt-5 space-y-4 text-white/78 text-[15px] lg:text-[16px] leading-relaxed">
                    {s.long.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>

                <div className={`lg:col-span-5 ${reversed ? 'lg:order-1' : ''}`}>
                  <div className="bg-[#0a1b35]/65 border border-[#e8b863]/22 rounded-2xl p-6 sm:p-7 h-full">
                    <div className="w-12 h-12 rounded-xl bg-[#e8b863] flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5 text-[#0a1b35]" strokeWidth={1.5} />
                    </div>
                    <p className="text-[11px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold mb-4">What we deliver</p>
                    <ul className="space-y-3">
                      {s.deliverables.map((d) => (
                        <li key={d} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#e8b863]/18 border border-[#e8b863]/40 flex items-center justify-center mt-0.5">
                            <Check className="w-2.5 h-2.5 text-[#e8b863]" strokeWidth={3.5} />
                          </span>
                          <span className="text-white/82 text-[14px] leading-relaxed">{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <CTABanner
        title="Need help scoping the right engagement?"
        subtitle="Tell us the outcome you need and we'll propose the right combination of service lines, sequenced for impact."
        button="Talk to a consultant"
      />
    </>
  );
}
